'use client';

import { useFirebaseAuth } from '@/components/auth/FirebaseAuthProvider';
import {
  getDefaultTaskPresetForTool,
  getTaskPresetFieldLabels,
  getTaskPresetsForTool,
} from '@/lib/ai-workflow-presets';
import type { ToolEntry } from '@/lib/tools';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

type AiWorkflowPlaceholderPanelProps = {
  entry: ToolEntry;
};

function AiWorkflowPlaceholderPanelInner({ entry }: AiWorkflowPlaceholderPanelProps) {
  const { user, session, isLoading, isSessionLoading } = useFirebaseAuth();
  const taskPresets = useMemo(() => getTaskPresetsForTool(entry.slug), [entry.slug]);
  const defaultTaskPreset = useMemo(() => getDefaultTaskPresetForTool(entry.slug), [entry.slug]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(
    defaultTaskPreset?.id ?? null,
  );
  const isSignedIn = Boolean(user);
  const canUseAi = Boolean(session?.account?.canUseAi);
  const nextPath = `/tools/${entry.slug}`;
  const primaryHref = !isSignedIn
    ? `/signup?next=${encodeURIComponent(nextPath)}`
    : `/account?next=${encodeURIComponent(nextPath)}`;
  const primaryLabel = !isSignedIn
    ? 'Create free account'
    : canUseAi
      ? 'Tool launch coming soon'
      : 'Open account';
  const statusLabel = entry.placeholderStatusLabel ?? 'Coming soon';
  const relatedToolHref =
    entry.relatedResources.find((resource) => resource.label === 'Tool')?.href ??
    '/tools/ai-workflows';
  const relatedToolLabel =
    entry.relatedResources.find((resource) => resource.label === 'Tool')?.title ??
    'Browse AI Workflows';
  const selectedPreset =
    taskPresets.find((preset) => preset.id === selectedPresetId) ?? defaultTaskPreset;

  useEffect(() => {
    setSelectedPresetId(defaultTaskPreset?.id ?? null);
  }, [defaultTaskPreset?.id, entry.slug]);

  return (
    <article className="project-detail-card discovery-detail-card tool-ai-placeholder">
      <div className="tool-ai-placeholder-top">
        <div className="article-meta-strip discovery-meta-strip">
          <span>{statusLabel}</span>
          <span>Free with a Cleanor account</span>
        </div>
        <div className="tool-ai-placeholder-status">
          <strong>{entry.cardTitle}</strong>
          <span>{entry.cardDescription}</span>
        </div>
      </div>

      <div className="tool-ai-placeholder-grid">
        <article className="overview-card tool-ai-placeholder-card">
          <h3>Inputs</h3>
          <ul className="detail-list detail-list-compact">
            {(entry.primaryInputs ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
        <article className="overview-card tool-ai-placeholder-card">
          <h3>Outputs</h3>
          <ul className="detail-list detail-list-compact">
            {(entry.outputs ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
        <article className="overview-card tool-ai-placeholder-card">
          <h3>Best for</h3>
          <ul className="detail-list detail-list-compact">
            {(entry.useCases ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
        <article className="overview-card tool-ai-placeholder-card">
          <h3>Capabilities</h3>
          <ul className="detail-list detail-list-compact">
            {(entry.capabilities ?? []).map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      </div>

      {selectedPreset ? (
        <section className="overview-card tool-ai-runtime-panel tool-ai-runtime-presets-panel">
          <div className="tool-ai-runtime-section-heading">
            <strong>Choose a task</strong>
            <span>Preview the specialist jobs this workflow is designed to handle.</span>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>Task</span>
            <select
              value={selectedPreset.id}
              onChange={(event) => setSelectedPresetId(event.target.value || null)}
            >
              {taskPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.title}
                </option>
              ))}
            </select>
            <small>
              The runtime is still coming, but the preset library already shows the expected
              jobs-to-be-done.
            </small>
          </label>
          <article className="tool-ai-runtime-preset-preview">
            <div className="tool-ai-runtime-preset-preview-copy">
              <strong>{selectedPreset.title}</strong>
              <p>{selectedPreset.description}</p>
              <span>{selectedPreset.recommendedFor}</span>
            </div>
            <div className="tool-ai-runtime-preset-preview-meta">
              <div className="tool-ai-runtime-preset-chips">
                {selectedPreset.tags.map((tag) => (
                  <span className="tool-ai-runtime-preset-chip" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
              <p>Fields included: {getTaskPresetFieldLabels(selectedPreset.values).join(', ')}</p>
              <p>Editable token: {selectedPreset.starterToken}</p>
            </div>
          </article>
        </section>
      ) : null}

      <div className="tool-ai-placeholder-cta">
        <div className="tool-ai-placeholder-copy">
          <h3>Access path</h3>
          <p>
            {isLoading || isSessionLoading
              ? 'Checking your current access state.'
              : !isSignedIn
                ? 'This workflow page is public now. AI workflows are free — just sign in with a Cleanor account, and the runtime unlocks in-place once it ships.'
                : canUseAi
                  ? 'AI access is active and free with your account. This workflow is visible now and will unlock in-place once the runtime ships.'
                  : 'Sign in with a Cleanor account to use AI workflows for free once the runtime ships.'}
          </p>
        </div>
        <div className="hero-actions">
          <Link className="button" href={primaryHref}>
            {primaryLabel}
          </Link>
          <Link className="button button-ghost button-ghost-light" href={relatedToolHref}>
            {relatedToolLabel}
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function AiWorkflowPlaceholderPanel(props: AiWorkflowPlaceholderPanelProps) {
  return <AiWorkflowPlaceholderPanelInner {...props} />;
}
